// @ts-nocheck
/**
 * LiveFeedPanel — Slide-out panel triggered by the 📡 button in XDialSidebar.
 *
 * Sections:
 *   1. Daily objectives — configurable RDV + call targets vs real data
 *   2. Live activity feed — recent events from allCalls (today)
 *   3. Streak / momentum — consecutive calls without a long pause
 *   4. Quick stats — session calls, connect rate, time on phone
 *
 * All data is computed from real `allCalls` and `activeCalls` from useDialer().
 * Objectives are user-configurable and persisted server-side (LiveFeedObjective).
 * If no calls today, empty/zero state is shown — no hardcoded values.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { formatDurationWords, formatRelativeTime } from '@/i18n/locale';
import {
  X, Target, Phone, Calendar, Flame, TrendingUp, Clock, Activity,
  PhoneCall, PhoneOff, PhoneMissed, CheckCircle2, Settings2,
  ChevronRight, Zap, BarChart2,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialerData } from '@/contexts/DialerDataContext';
import { useDialerAuth } from '@/contexts/DialerAuthContext';
import { hasMeetingBooked } from '@/utils/callUtils';
import { useTranslation } from 'react-i18next';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { api } from '@/api/api-client';
import { motion, AnimatePresence } from 'framer-motion';

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_OBJECTIVES = {
  rdvTarget: 3,
  callsTarget: 50,
};


const OUTCOME_META = {
  meeting_booked:     { icon: CheckCircle2, color: 'text-emerald-500' },
  connected_to_agent: { icon: PhoneCall,    color: 'text-blue-500'    },
  contacted:          { icon: PhoneCall,    color: 'text-blue-400'    },
  callback_requested: { icon: Clock,        color: 'text-amber-500'   },
  callback_scheduled: { icon: Clock,        color: 'text-amber-400'   },
  no_answer:          { icon: PhoneMissed,  color: 'text-muted-foreground' },
  not_interested:     { icon: PhoneOff,     color: 'text-muted-foreground' },
  not_reachable:      { icon: PhoneOff,     color: 'text-muted-foreground' },
  voicemail:          { icon: PhoneOff,     color: 'text-muted-foreground' },
  other:              { icon: Phone,        color: 'text-muted-foreground' },
  dialing:            { icon: Phone,        color: 'text-blue-500'    },
  ringing:            { icon: Phone,        color: 'text-blue-500'    },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayStart() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}


/** Format seconds as "Xh Xmin" or "Xmin Xs" */

/** Compute calling streak from today's calls sorted by time. */
function computeStreak(todayCalls) {
  if (!todayCalls.length) return { current: 0, best: 0 };

  // Sort ascending by start time
  const sorted = [...todayCalls].sort(
    (a, b) => new Date(a.started_at || a.created_date) - new Date(b.started_at || b.created_date),
  );

  const GAP_THRESHOLD_MS = 10 * 60 * 1000; // 10-minute gap breaks a streak
  let current = 1;
  let best = 1;
  let streak = 1;

  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1].started_at || sorted[i - 1].created_date);
    const curr = new Date(sorted[i].started_at || sorted[i].created_date);
    const gap = curr - prev;
    if (gap <= GAP_THRESHOLD_MS) {
      streak++;
    } else {
      streak = 1;
    }
    if (streak > best) best = streak;
    current = streak;
  }

  return { current, best };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ProgressBar({ value, max, colorClass = 'bg-primary' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="relative h-2 rounded-full overflow-hidden" style={{ background: 'hsl(var(--secondary))' }}>
      <div
        className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${colorClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ObjectiveRow({ icon: Icon, label, value, target, colorClass, unit = '' }) {
  const pct = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0;
  const reached = value >= target && target > 0;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon
            className="w-3.5 h-3.5 flex-shrink-0"
            style={{ color: reached ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))' }}
          />
          <span className="text-[11px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{label}</span>
        </div>
        <div className="flex items-center gap-1">
          <span
            className="font-data text-xs font-bold"
            style={{ color: reached ? 'hsl(var(--primary))' : 'hsl(var(--foreground))' }}
          >
            {value}
          </span>
          <span className="font-data text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
            / {target}{unit}
          </span>
          {reached && (
            <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
          )}
        </div>
      </div>
      <ProgressBar value={value} max={target} colorClass={reached ? 'bg-emerald-500' : colorClass} />
      <div className="text-right">
        <span className="font-data text-[9px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
          {pct}%
        </span>
      </div>
    </div>
  );
}

function FeedEvent({ call }) {
  const { t } = useTranslation('dialer');
  const outcome = call.call_outcome || call.call_status || 'other';
  const meta = OUTCOME_META[outcome] || OUTCOME_META.other;
  const Icon = meta.icon;
  const ts = call.ended_at || call.connected_at || call.started_at || call.created_date;

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-start gap-2.5 py-2"
      style={{ borderBottom: '1px solid hsl(var(--border) / 0.5)' }}
    >
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-full"
        style={{ width: 26, height: 26, background: 'hsl(var(--secondary))' }}
      >
        <Icon className={`w-3 h-3 ${meta.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-1">
          <span className="text-[11px] font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>
            {call.prospect_name || call.prospect_phone || t('dialer:liveFeed.unknownContact')}
          </span>
          <span className="flex-shrink-0 font-data text-[9px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
            {formatRelativeTime(ts)}
          </span>
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className={`text-[10px] ${meta.color}`}>{outcomeLabel(outcome, t)}</span>
          {call.prospect_company && (
            <>
              <span className="text-[9px]" style={{ color: 'hsl(var(--muted-foreground))' }}>·</span>
              <span className="text-[10px] truncate" style={{ color: 'hsl(var(--muted-foreground))' }}>
                {call.prospect_company}
              </span>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div
      className="flex flex-col gap-1 p-2.5 rounded-lg"
      style={{ background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))' }}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color: 'hsl(var(--muted-foreground))' }} />
        <span className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{label}</span>
      </div>
      <span className="font-data text-base font-bold" style={{ color: 'hsl(var(--foreground))' }}>
        {value}
      </span>
      {sub && (
        <span className="text-[9px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{sub}</span>
      )}
    </div>
  );
}

// ── ObjectivesEditor ──────────────────────────────────────────────────────────

function ObjectivesEditor({ objectives, onChange, onClose, t }) {
  const [rdv, setRdv] = useState(String(objectives.rdvTarget));
  const [calls, setCalls] = useState(String(objectives.callsTarget));

  const handleSave = () => {
    const rdvVal = parseInt(rdv, 10);
    const callsVal = parseInt(calls, 10);
    if (isNaN(rdvVal) || rdvVal < 1 || isNaN(callsVal) || callsVal < 1) return;
    onChange({ rdvTarget: rdvVal, callsTarget: callsVal });
    onClose();
  };

  return (
    <div
      className="rounded-lg p-3 flex flex-col gap-3"
      style={{ background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))' }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
          {t('dialer:liveFeed.editObjectives')}
        </span>
        <button
          onClick={onClose}
          className="flex items-center justify-center"
          style={{ width: 20, height: 20, borderRadius: 4, border: 'none', background: 'transparent', cursor: 'pointer' }}
        >
          <X className="w-3 h-3" style={{ color: 'hsl(var(--muted-foreground))' }} />
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{t('dialer:liveFeed.rdvTargetPerDay')}</span>
          <input
            type="number"
            min={1}
            max={99}
            value={rdv}
            onChange={(e) => setRdv(e.target.value)}
            className="text-xs rounded px-2 py-1 font-data"
            style={{
              border: '1px solid hsl(var(--border))',
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              outline: 'none',
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{t('dialer:liveFeed.callsTargetPerDay')}</span>
          <input
            type="number"
            min={1}
            max={999}
            value={calls}
            onChange={(e) => setCalls(e.target.value)}
            className="text-xs rounded px-2 py-1 font-data"
            style={{
              border: '1px solid hsl(var(--border))',
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              outline: 'none',
            }}
          />
        </label>
      </div>
      <button
        onClick={handleSave}
        className="text-xs font-semibold rounded px-3 py-1.5 transition-opacity hover:opacity-90"
        style={{
          background: 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        {t('dialer:liveFeed.save')}
      </button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function LiveFeedPanel({ isOpen, onClose }) {
  const { t } = useTranslation('dialer');
  const { allCalls, activeCalls } = useDialerData();
  const { callerData } = useDialerAuth();
  const clientEmail = callerData?.client_email;
  const queryClient = useQueryClient();
  const [showEditor, setShowEditor] = useState(false);
  const [feedLimit, setFeedLimit] = useState(20);

  // Objectives persisted as LiveFeedObjective rows (one per key: "rdvTarget", "callsTarget").
  const { data: objectiveRows = [] } = useQuery({
    queryKey: ['live-feed-objectives', clientEmail],
    queryFn: () => api.entities.LiveFeedObjective.filter({}, 'order', 50),
    enabled: !!clientEmail,
  });
  const objectives = useMemo(() => {
    const out = { ...DEFAULT_OBJECTIVES };
    for (const row of objectiveRows) {
      if (row.key === 'rdvTarget') out.rdvTarget = Number(row.target) || DEFAULT_OBJECTIVES.rdvTarget;
      else if (row.key === 'callsTarget') out.callsTarget = Number(row.target) || DEFAULT_OBJECTIVES.callsTarget;
    }
    return out;
  }, [objectiveRows]);

  const handleObjectivesChange = useCallback(async (next) => {
    if (!clientEmail) return;
    const upsert = async (key, label, target) => {
      const existing = objectiveRows.find((r) => r.key === key);
      if (existing?.id) {
        await api.entities.LiveFeedObjective.update(existing.id, { target, label, enabled: true });
      } else {
        await api.entities.LiveFeedObjective.create({
          key, label, target, enabled: true, order: key === 'rdvTarget' ? 0 : 1,
        });
      }
    };
    try {
      await Promise.all([
        upsert('rdvTarget', 'Objectif RDV', Number(next.rdvTarget) || 0),
        upsert('callsTarget', 'Objectif appels', Number(next.callsTarget) || 0),
      ]);
      queryClient.invalidateQueries({ queryKey: ['live-feed-objectives', clientEmail] });
    } catch {
      // non-blocking
    }
  }, [clientEmail, objectiveRows, queryClient]);

  // ── Today's data ────────────────────────────────────────────────────────────
  const start = useMemo(() => todayStart(), []);

  const todayCalls = useMemo(
    () => allCalls.filter((c) => new Date(c.started_at || c.created_date) >= start),
    [allCalls, start],
  );

  const todayMeetings = useMemo(
    () => todayCalls.filter(hasMeetingBooked),
    [todayCalls],
  );

  const todayConnected = useMemo(
    () =>
      todayCalls.filter((c) =>
        hasMeetingBooked(c) ||
        ['connected_to_agent', 'contacted', 'callback_requested', 'callback_scheduled', 'not_interested'].includes(
          c.call_outcome,
        ),
      ),
    [todayCalls],
  );

  const connectRate = useMemo(() => {
    if (!todayCalls.length) return 0;
    return Math.round((todayConnected.length / todayCalls.length) * 100);
  }, [todayCalls, todayConnected]);

  // Total time on phone today (sum of duration_seconds for completed connected calls)
  const timeOnPhoneSec = useMemo(() => {
    return todayCalls.reduce((acc, c) => {
      const dur = c.duration_seconds || 0;
      return acc + dur;
    }, 0);
  }, [todayCalls]);

  // ── Activity feed (today, sorted desc) ─────────────────────────────────────
  const feedEvents = useMemo(() => {
    // Start from recent active calls + completed today calls with meaningful outcomes
    const completed = todayCalls
      .filter((c) => c.call_outcome || ['completed', 'failed'].includes(c.call_status))
      .sort((a, b) => {
        const ta = new Date(a.ended_at || a.connected_at || a.started_at || a.created_date);
        const tb = new Date(b.ended_at || b.connected_at || b.started_at || b.created_date);
        return tb - ta;
      });

    const activeForFeed = activeCalls.map((c) => ({ ...c, _isActive: true }));

    return [...activeForFeed, ...completed].slice(0, feedLimit);
  }, [todayCalls, activeCalls, feedLimit]);

  // ── Streak ─────────────────────────────────────────────────────────────────
  const streak = useMemo(() => computeStreak(todayCalls), [todayCalls]);

  // ── Close on Escape ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="livefeed-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 400,
              background: 'rgba(0,0,0,0.3)',
            }}
          />

          {/* Panel */}
          <motion.div
            key="livefeed-panel"
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 340, damping: 32 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              width: 320,
              zIndex: 401,
              background: 'hsl(var(--card))',
              borderRight: '1px solid hsl(var(--border))',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* ── Header ── */}
            <div
              className="flex items-center justify-between flex-shrink-0"
              style={{
                padding: '14px 16px 12px',
                borderBottom: '1px solid hsl(var(--border))',
              }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="flex items-center justify-center rounded-lg"
                  style={{ width: 30, height: 30, background: 'hsl(var(--primary) / 0.08)' }}
                >
                  <Activity className="w-4 h-4" style={{ color: 'hsl(var(--primary))' }} />
                </div>
                <div>
                  <div className="text-sm font-bold" style={{ color: 'hsl(var(--foreground))' }}>
                    {t('dialer:liveFeed.title')}
                  </div>
                  <div className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {t('dialer:liveFeed.subtitle')}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="flex items-center justify-center rounded-lg border border-border hover:border-foreground transition-colors"
                style={{ width: 28, height: 28, background: 'transparent', cursor: 'pointer' }}
              >
                <X className="w-3.5 h-3.5" style={{ color: 'hsl(var(--muted-foreground))' }} />
              </button>
            </div>

            {/* ── Scrollable body ── */}
            <div className="flex-1 overflow-y-auto" style={{ padding: '14px 16px', gap: 0 }}>

              {/* ── Section 1: Daily Objectives ── */}
              <div className="flex flex-col gap-3 mb-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5" style={{ color: 'hsl(var(--primary))' }} />
                    <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--foreground))' }}>
                      {t('dialer:liveFeed.todayObjectives')}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowEditor((v) => !v)}
                    title={t('dialer:liveFeed.editObjectives')}
                    className="flex items-center justify-center rounded hover:opacity-70 transition-opacity"
                    style={{ width: 22, height: 22, background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    <Settings2 className="w-3 h-3" style={{ color: 'hsl(var(--muted-foreground))' }} />
                  </button>
                </div>

                <AnimatePresence>
                  {showEditor && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.15 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <ObjectivesEditor
                        objectives={objectives}
                        onChange={handleObjectivesChange}
                        onClose={() => setShowEditor(false)}
                        t={t}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <ObjectiveRow
                  icon={Calendar}
                  label={t('dialer:liveFeed.meetingsBooked')}
                  value={todayMeetings.length}
                  target={objectives.rdvTarget}
                  colorClass="bg-primary"
                />
                <ObjectiveRow
                  icon={Phone}
                  label={t('dialer:liveFeed.callsMade')}
                  value={todayCalls.length}
                  target={objectives.callsTarget}
                  colorClass="bg-blue-500"
                />
              </div>

              {/* ── Divider ── */}
              <div style={{ height: 1, background: 'hsl(var(--border))', marginBottom: 16 }} />

              {/* ── Section 2: Quick Stats ── */}
              <div className="flex flex-col gap-3 mb-5">
                <div className="flex items-center gap-1.5">
                  <BarChart2 className="w-3.5 h-3.5" style={{ color: 'hsl(var(--primary))' }} />
                  <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--foreground))' }}>
                    {t('dialer:liveFeed.todayStats')}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <StatCard
                    icon={PhoneCall}
                    label={t('dialer:liveFeed.calls')}
                    value={todayCalls.length}
                    sub={todayCalls.length === 0 ? t('dialer:liveFeed.noCalls') : t('dialer:liveFeed.connectedCount', { count: todayConnected.length })}
                  />
                  <StatCard
                    icon={TrendingUp}
                    label={t('dialer:liveFeed.contactRate')}
                    value={`${connectRate}%`}
                    sub={todayCalls.length > 0 ? `${todayConnected.length}/${todayCalls.length}` : t('dialer:liveFeed.noData')}
                  />
                  <StatCard
                    icon={CheckCircle2}
                    label={t('dialer:liveFeed.meetings')}
                    value={todayMeetings.length}
                    sub={todayMeetings.length === 0 ? t('dialer:liveFeed.noMeetings') : t('dialer:liveFeed.targetPrefix', { target: objectives.rdvTarget })}
                  />
                  <StatCard
                    icon={Clock}
                    label={t('dialer:liveFeed.timeOnPhone')}
                    value={formatDurationWords(timeOnPhoneSec)}
                    sub={timeOnPhoneSec === 0 ? t('dialer:liveFeed.noData') : undefined}
                  />
                </div>
              </div>

              {/* ── Section 3: Streak ── */}
              {(streak.current > 1 || streak.best > 1) && (
                <>
                  <div style={{ height: 1, background: 'hsl(var(--border))', marginBottom: 16 }} />
                  <div className="flex flex-col gap-3 mb-5">
                    <div className="flex items-center gap-1.5">
                      <Flame className="w-3.5 h-3.5 text-orange-500" />
                      <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--foreground))' }}>
                        {t('dialer:liveFeed.momentum')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div
                        className="flex flex-col gap-1 p-2.5 rounded-lg"
                        style={{ background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))' }}
                      >
                        <div className="flex items-center gap-1">
                          <Zap className="w-3 h-3 text-orange-500" />
                          <span className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{t('dialer:liveFeed.currentStreak')}</span>
                        </div>
                        <span className="font-data text-base font-bold" style={{ color: 'hsl(var(--foreground))' }}>
                          {streak.current}
                        </span>
                        <span className="text-[9px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{t('dialer:liveFeed.consecutiveCalls')}</span>
                      </div>
                      <div
                        className="flex flex-col gap-1 p-2.5 rounded-lg"
                        style={{ background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))' }}
                      >
                        <div className="flex items-center gap-1">
                          <Flame className="w-3 h-3 text-orange-400" />
                          <span className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{t('dialer:liveFeed.bestStreak')}</span>
                        </div>
                        <span className="font-data text-base font-bold" style={{ color: 'hsl(var(--foreground))' }}>
                          {streak.best}
                        </span>
                        <span className="text-[9px]" style={{ color: 'hsl(var(--muted-foreground))' }}>{t('dialer:liveFeed.today')}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── Divider ── */}
              <div style={{ height: 1, background: 'hsl(var(--border))', marginBottom: 16 }} />

              {/* ── Section 4: Live Activity Feed ── */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5" style={{ color: 'hsl(var(--primary))' }} />
                  <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--foreground))' }}>
                    {t('dialer:liveFeed.recentActivity')}
                  </span>
                  {activeCalls.length > 0 && (
                    <span
                      className="font-data text-[9px] font-bold rounded-full px-1.5 py-0.5"
                      style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
                    >
                      {activeCalls.length} {t('dialer:liveFeed.live')}
                    </span>
                  )}
                </div>

                {feedEvents.length === 0 ? (
                  <div
                    className="flex flex-col items-center justify-center rounded-lg py-8 gap-2"
                    style={{ background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))' }}
                  >
                    <Phone className="w-6 h-6" style={{ color: 'hsl(var(--muted-foreground) / 0.4)' }} />
                    <span className="text-[11px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
                      {t('dialer:liveFeed.noCallsToday')}
                    </span>
                    <span className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground) / 0.6)' }}>
                      {t('dialer:liveFeed.callsWillAppear')}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {feedEvents.map((call) => (
                      <FeedEvent key={call.id} call={call} />
                    ))}
                    {todayCalls.length > feedLimit && (
                      <button
                        onClick={() => setFeedLimit((l) => l + 20)}
                        className="flex items-center justify-center gap-1 py-2 mt-1 text-[11px] transition-opacity hover:opacity-70"
                        style={{ color: 'hsl(var(--primary))', background: 'transparent', border: 'none', cursor: 'pointer' }}
                      >
                        {t('dialer:liveFeed.seeMore')}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Bottom padding */}
              <div style={{ height: 24 }} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
